import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, Search, X } from "lucide-react";
import api from "@/lib/api";

const financialStatuses = ["", "paid", "pending", "partially_paid", "refunded", "partially_refunded", "voided", "authorized"];
const fulfillmentStatuses = ["", "fulfilled", "partial", "unfulfilled", "restocked"];
const sortOptions = [
    { key: "-created_at", label: "Newest first" },
    { key: "created_at", label: "Oldest first" },
    { key: "-updated_at", label: "Recently updated" },
    { key: "-total_price", label: "Highest total" },
    { key: "total_price", label: "Lowest total" },
];

function money(n, currency = "USD") {
    try {
        return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(n) || 0);
    } catch {
        return `$${(Number(n) || 0).toFixed(2)}`;
    }
}

function statusPill(label, tone) {
    const toneMap = {
        paid: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
        pending: "bg-yellow-500/15 text-yellow-700",
        refunded: "bg-[hsl(var(--rust))]/15 text-[hsl(var(--rust))]",
        cancelled: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]",
        fulfilled: "bg-[hsl(var(--primary))]/25 text-[hsl(var(--ink))]",
        unfulfilled: "bg-[hsl(var(--dust-300))] text-[hsl(var(--ink-soft))]",
    };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-heading uppercase tracking-wider ${toneMap[tone] || "bg-[hsl(var(--dust-300))] text-[hsl(var(--ink))]"}`}>
            {label}
        </span>
    );
}

export default function AdminOrders() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [shopify, setShopify] = useState({});
    const [q, setQ] = useState("");
    const [financial, setFinancial] = useState("");
    const [fulfillment, setFulfillment] = useState("");
    const [cancelled, setCancelled] = useState("");
    const [sort, setSort] = useState("-created_at");
    const [detail, setDetail] = useState(null);
    const [reconciling, setReconciling] = useState(false);

    const load = async () => {
        setLoading(true);
        setErrorMsg("");
        try {
            const params = { sort, limit: 200 };
            if (q) params.q = q;
            if (financial) params.financial_status = financial;
            if (fulfillment) params.fulfillment_status = fulfillment;
            if (cancelled === "yes") params.cancelled = true;
            if (cancelled === "no") params.cancelled = false;
            const { data } = await api.get("/admin/orders", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setShopify(data.shopify || {});
        } catch (e) {
            const status = e.response?.status;
            if (status === 401 || status === 403) setErrorMsg("You don't have permission to view orders.");
            else setErrorMsg("Couldn't load orders — try refreshing.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort, financial, fulfillment, cancelled]);

    const onSearch = (e) => { e.preventDefault(); load(); };

    const reconcile = async () => {
        if (reconciling) return;
        setReconciling(true);
        try {
            const { data } = await api.post("/admin/shopify/reconcile-orders", {});
            toast.success(`Reconcile: ${data.created} new, ${data.updated} updated, ${data.skipped} skipped, ${data.failed} failed (fetched ${data.fetched})`);
            await load();
        } catch (e) {
            const detail = e.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || "Reconcile failed";
            toast.error(msg);
        } finally {
            setReconciling(false);
        }
    };

    const openDetail = async (id) => {
        try {
            const { data } = await api.get(`/admin/orders/${id}`);
            setDetail(data);
        } catch {
            toast.error("Couldn't load order detail");
        }
    };

    const shopifyConfigured = shopify.admin_enabled && shopify.webhook_enabled;

    return (
        <div data-testid="admin-orders">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">Sales</div>
                    <h1 className="mt-1 font-display text-4xl uppercase italic text-[hsl(var(--ink))]">Orders</h1>
                    <p className="mt-1 font-mono-brand text-xs text-[hsl(var(--ink-soft))]">
                        {total} order{total === 1 ? "" : "s"} · Shopify is the system of record for payment &amp; fulfillment.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {!shopifyConfigured && (
                        <div className="rounded border-2 border-[hsl(var(--rust))] bg-[hsl(var(--rust))]/10 px-3 py-2 font-mono-brand text-xs text-[hsl(var(--rust))]" data-testid="shopify-not-configured-banner">
                            Shopify Admin / Webhook credentials not yet configured — connect them in .env to enable live sync.
                        </div>
                    )}
                    <button
                        onClick={reconcile}
                        disabled={reconciling || !shopify.admin_enabled}
                        className="inline-flex items-center gap-2 border-2 border-[hsl(var(--ink))] bg-[hsl(var(--primary))] px-4 py-2 font-heading text-xs uppercase tracking-wider disabled:opacity-50"
                        data-testid="reconcile-orders-btn"
                        title={shopify.admin_enabled ? "Fetch orders updated since last sync" : "Admin API not configured"}
                    >
                        <RefreshCw className={`h-4 w-4 ${reconciling ? "animate-spin" : ""}`} />
                        {reconciling ? "Syncing…" : "Sync missed orders"}
                    </button>
                </div>
            </div>

            <form onSubmit={onSearch} className="mt-6 flex flex-wrap gap-3 border-2 border-[hsl(var(--ink))] bg-card p-4">
                <div className="flex flex-1 min-w-[220px] items-center gap-2 border border-[hsl(var(--dust-300))] bg-[hsl(var(--dust-50))] px-3">
                    <Search className="h-4 w-4 text-[hsl(var(--ink-soft))]" />
                    <input
                        placeholder="Search order #, email, SKU, or name"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="w-full bg-transparent py-2 font-mono-brand text-sm focus:outline-none"
                        data-testid="orders-search"
                    />
                    {q && <button type="button" onClick={() => { setQ(""); setTimeout(load, 0); }} className="text-[hsl(var(--ink-soft))]" data-testid="orders-search-clear"><X className="h-4 w-4" /></button>}
                </div>
                <select value={financial} onChange={(e) => setFinancial(e.target.value)} className="border border-[hsl(var(--dust-300))] bg-[hsl(var(--dust-50))] px-3 py-2 font-mono-brand text-xs uppercase" data-testid="orders-financial-filter">
                    {financialStatuses.map((s) => <option key={s} value={s}>{s ? `Payment: ${s}` : "Payment: any"}</option>)}
                </select>
                <select value={fulfillment} onChange={(e) => setFulfillment(e.target.value)} className="border border-[hsl(var(--dust-300))] bg-[hsl(var(--dust-50))] px-3 py-2 font-mono-brand text-xs uppercase" data-testid="orders-fulfillment-filter">
                    {fulfillmentStatuses.map((s) => <option key={s} value={s}>{s ? `Fulfillment: ${s}` : "Fulfillment: any"}</option>)}
                </select>
                <select value={cancelled} onChange={(e) => setCancelled(e.target.value)} className="border border-[hsl(var(--dust-300))] bg-[hsl(var(--dust-50))] px-3 py-2 font-mono-brand text-xs uppercase" data-testid="orders-cancelled-filter">
                    <option value="">Cancelled: any</option>
                    <option value="yes">Cancelled: yes</option>
                    <option value="no">Cancelled: no</option>
                </select>
                <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-[hsl(var(--dust-300))] bg-[hsl(var(--dust-50))] px-3 py-2 font-mono-brand text-xs uppercase" data-testid="orders-sort">
                    {sortOptions.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <button type="submit" className="border-2 border-[hsl(var(--ink))] bg-[hsl(var(--ink))] px-4 py-2 font-heading text-xs uppercase tracking-wider text-[hsl(var(--dust-50))]" data-testid="orders-search-submit">Search</button>
            </form>

            <div className="mt-6 overflow-x-auto border-2 border-[hsl(var(--ink))] bg-card">
                <table className="w-full font-mono-brand text-xs">
                    <thead className="bg-[hsl(var(--ink))] text-[hsl(var(--dust-50))]">
                        <tr>{["Order", "When", "Customer", "Total", "Payment", "Fulfillment", "Actions"].map((h) => (
                            <th key={h} className="px-3 py-2 text-left font-heading text-xs uppercase tracking-wider">{h}</th>
                        ))}</tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={7} className="px-3 py-6 text-center text-[hsl(var(--ink-soft))]" data-testid="orders-loading">Loading…</td></tr>
                        )}
                        {!loading && errorMsg && (
                            <tr><td colSpan={7} className="px-3 py-6 text-center text-[hsl(var(--danger))]" data-testid="orders-error">{errorMsg}</td></tr>
                        )}
                        {!loading && !errorMsg && items.length === 0 && (
                            <tr><td colSpan={7} className="px-3 py-6 text-center text-[hsl(var(--ink-soft))]" data-testid="orders-empty">
                                No orders yet. Once Shopify webhooks are configured, orders will appear here automatically.
                            </td></tr>
                        )}
                        {!loading && !errorMsg && items.map((o) => (
                            <tr key={o.shopify_order_id} className="border-t border-[hsl(var(--dust-300))] hover:bg-[hsl(var(--dust-50))]" data-testid={`order-row-${o.shopify_order_id}`}>
                                <td className="px-3 py-2 font-semibold text-[hsl(var(--ink))]">{o.name || `#${o.shopify_order_number}`}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-[hsl(var(--ink-soft))]">{o.created_at ? new Date(o.created_at).toLocaleString() : "—"}</td>
                                <td className="px-3 py-2">
                                    <div>{[o.customer?.first_name, o.customer?.last_name].filter(Boolean).join(" ") || "—"}</div>
                                    <div className="text-[hsl(var(--ink-soft))]">{o.email || ""}</div>
                                </td>
                                <td className="px-3 py-2 font-semibold">{money(o.total_price, o.currency)}</td>
                                <td className="px-3 py-2">{statusPill(o.financial_status || "—", o.financial_status)}</td>
                                <td className="px-3 py-2">{o.cancelled_at ? statusPill("cancelled", "cancelled") : statusPill(o.fulfillment_status || "unfulfilled", o.fulfillment_status || "unfulfilled")}</td>
                                <td className="px-3 py-2">
                                    <div className="flex gap-2">
                                        <button onClick={() => openDetail(o.shopify_order_id)} className="text-[hsl(var(--ink))] underline" data-testid={`order-view-${o.shopify_order_id}`}>View</button>
                                        {o.admin_url && (
                                            <a href={o.admin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]" data-testid={`order-shopify-${o.shopify_order_id}`}>
                                                Shopify <ExternalLink className="h-3 w-3" />
                                            </a>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {detail && <OrderDetailDrawer order={detail} onClose={() => setDetail(null)} />}
        </div>
    );
}

function OrderDetailDrawer({ order, onClose }) {
    const cur = order.currency || "USD";
    const cancelled = !!order.cancelled_at;
    return (
        <div className="fixed inset-0 z-50 flex" data-testid="order-detail-drawer">
            <div className="flex-1 bg-black/40" onClick={onClose} />
            <div className="w-full max-w-2xl overflow-y-auto bg-[hsl(var(--dust-50))] p-8 shadow-2xl">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">Sales order</div>
                        <h2 className="mt-1 font-display text-3xl uppercase italic text-[hsl(var(--ink))]">{order.name || `#${order.shopify_order_number}`}</h2>
                        <div className="mt-1 font-mono-brand text-xs text-[hsl(var(--ink-soft))]">Shopify order id: {order.shopify_order_id}</div>
                    </div>
                    <button onClick={onClose} className="border-2 border-[hsl(var(--ink))] p-2" data-testid="order-drawer-close"><X className="h-4 w-4" /></button>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                    {statusPill(`payment: ${order.financial_status || "—"}`, order.financial_status)}
                    {cancelled
                        ? statusPill(`cancelled: ${order.cancel_reason || "yes"}`, "cancelled")
                        : statusPill(`fulfillment: ${order.fulfillment_status || "unfulfilled"}`, order.fulfillment_status || "unfulfilled")}
                    {order.refund_amount > 0 && statusPill(`refunded: ${money(order.refund_amount, cur)}`, "refunded")}
                    {order.admin_url && (
                        <a href={order.admin_url} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-xs font-heading uppercase tracking-wider text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]">
                            Open in Shopify <ExternalLink className="h-3 w-3" />
                        </a>
                    )}
                </div>

                <section className="mt-6 border-2 border-[hsl(var(--ink))] bg-card p-4">
                    <h3 className="font-heading text-sm uppercase tracking-wider">Customer</h3>
                    <div className="mt-2 font-mono-brand text-sm">
                        <div>{[order.customer?.first_name, order.customer?.last_name].filter(Boolean).join(" ") || "—"}</div>
                        <div>{order.email || "—"}</div>
                        <div>{order.phone || ""}</div>
                    </div>
                </section>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <section className="border-2 border-[hsl(var(--ink))] bg-card p-4">
                        <h3 className="font-heading text-sm uppercase tracking-wider">Shipping</h3>
                        <AddressBlock a={order.shipping_address} />
                    </section>
                    <section className="border-2 border-[hsl(var(--ink))] bg-card p-4">
                        <h3 className="font-heading text-sm uppercase tracking-wider">Billing</h3>
                        <AddressBlock a={order.billing_address} />
                    </section>
                </div>

                <section className="mt-4 border-2 border-[hsl(var(--ink))] bg-card p-4">
                    <h3 className="font-heading text-sm uppercase tracking-wider">Line items</h3>
                    <table className="mt-2 w-full font-mono-brand text-xs" data-testid="order-line-items">
                        <thead className="text-[hsl(var(--ink-soft))]">
                            <tr>{["Item", "SKU", "Qty", "Unit", "Line"].map((h) => (<th key={h} className="py-1 text-left font-heading uppercase">{h}</th>))}</tr>
                        </thead>
                        <tbody>
                            {(order.line_items || []).map((li) => (
                                <tr key={li.id} className="border-t border-[hsl(var(--dust-300))]">
                                    <td className="py-1.5">
                                        <div>{li.name}</div>
                                        {li.variant_title && <div className="text-[hsl(var(--ink-soft))]">{li.variant_title}</div>}
                                    </td>
                                    <td className="py-1.5">{li.sku || "—"}</td>
                                    <td className="py-1.5">{li.quantity}</td>
                                    <td className="py-1.5">{money(li.unit_price, cur)}</td>
                                    <td className="py-1.5">{money((li.unit_price || 0) * (li.quantity || 0) - (li.total_discount || 0), cur)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>

                <section className="mt-4 border-2 border-[hsl(var(--ink))] bg-card p-4">
                    <h3 className="font-heading text-sm uppercase tracking-wider">Totals</h3>
                    <dl className="mt-2 space-y-1 font-mono-brand text-sm">
                        <Row k="Subtotal" v={money(order.subtotal_price, cur)} />
                        <Row k="Shipping" v={money(order.total_shipping, cur)} />
                        <Row k="Discounts" v={`− ${money(order.total_discounts, cur)}`} />
                        <Row k="Tax" v={money(order.total_tax, cur)} />
                        <Row k="Total" v={money(order.total_price, cur)} bold />
                        {order.refund_amount > 0 && <Row k="Refunded" v={`− ${money(order.refund_amount, cur)}`} />}
                    </dl>
                </section>

                {(order.tracking || []).length > 0 && (
                    <section className="mt-4 border-2 border-[hsl(var(--ink))] bg-card p-4">
                        <h3 className="font-heading text-sm uppercase tracking-wider">Tracking</h3>
                        <ul className="mt-2 space-y-1 font-mono-brand text-xs">
                            {order.tracking.map((t, i) => (
                                <li key={i}>
                                    {t.company ? `${t.company} · ` : ""}
                                    {t.url ? <a href={t.url} target="_blank" rel="noreferrer" className="underline">{t.number}</a> : t.number}
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                <div className="mt-6 font-mono-brand text-[10px] text-[hsl(var(--ink-soft))]">
                    Last synced: {order.last_synced_at ? new Date(order.last_synced_at).toLocaleString() : "—"}
                    {order.correlation_id && <> · Correlation: {order.correlation_id}</>}
                </div>
            </div>
        </div>
    );
}

function AddressBlock({ a }) {
    if (!a || !Object.keys(a).length) return <div className="mt-2 font-mono-brand text-xs text-[hsl(var(--ink-soft))]">—</div>;
    return (
        <address className="mt-2 not-italic font-mono-brand text-xs">
            <div>{[a.first_name, a.last_name].filter(Boolean).join(" ")}</div>
            {a.company && <div>{a.company}</div>}
            {a.address1 && <div>{a.address1}</div>}
            {a.address2 && <div>{a.address2}</div>}
            <div>{[a.city, a.province_code || a.province, a.zip].filter(Boolean).join(", ")}</div>
            <div>{a.country}</div>
            {a.phone && <div>{a.phone}</div>}
        </address>
    );
}

function Row({ k, v, bold }) {
    return (
        <div className="flex justify-between">
            <dt className="text-[hsl(var(--ink-soft))]">{k}</dt>
            <dd className={bold ? "font-semibold text-[hsl(var(--ink))]" : ""}>{v}</dd>
        </div>
    );
}
